import { userDeviceEventsByTypesAndTimeRange } from '@/api';
import { SENSORS } from '@/constants/device';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { forEach, get, isEmpty, isNumber, isString, last, nth, size, toNumber } from 'lodash';
import { DateTime } from 'luxon';
import moment from 'moment';

export const postalCodeStatus = {
  empty: 'EMPTY',
  invalid: 'INVALID',
  valid: 'VALID',
  noLocationKey: 'No location key',
};

export const envEventTypes = {
  BATTERY: 'sensor_battery_report',
  HUMIDITY: 'sensor_humidity_report',
  LIGHT: 'sensor_light_report',
  TEMPERATURE: 'sensor_temperature_report',
};

export const envEventDataKeys = {
  DEVICE_UUID: 'deviceuuid',
  ENTITY_UUID: 'entity_uuid',
  MODEL_NUMBER: 'modelnumber',
  TIMESTAMP: 'utc_timestamp_inms',
  LOCATION: 'location',
  CONNECTED: 'connected',
  MESSAGE_TYPE: 'message_type',
  MESSAGE_ID: 'message_id',
};

export const convertTemperature = (value, currentUnit, targetUnit) => {
  try {
    if (value === undefined) return null;
    if (currentUnit === targetUnit) return Number(value.toFixed(1));
    const toCelsius = (fahrenheit) => Number((((fahrenheit - 32) * 5) / 9).toFixed(1));
    const toFahrenheit = (celsius) => Number(((celsius * 9) / 5 + 32).toFixed(1));

    return targetUnit === 'C' ? toCelsius(value) : toFahrenheit(value);
  } catch (error) {
    //handle when value is - or anything else
    return '-';
  }
};

export const sortByModelAndLocation = (a, b) => {
  const locationA = a[1]?.location ?? '';
  const locationB = b[1]?.location ?? '';

  if (locationA === null || locationA === undefined) return -1;
  if (locationB === null || locationB === undefined) return 1;

  return locationA.localeCompare(locationB);
};

// export const mapEventData = (eventData) => ({
//   batteryLevel: eventData.battery_level,
//   temperature: eventData.environment_status__temperature,
//   tempUnit: eventData.environment_status__temperature_unit,
//   humidity: eventData.environment_status__humidity,
//   connectionStatus: eventData.connected ? "CONNECTED" : "DISCONNECTED",
//   ambientLight: eventData.environment_status__ambient_light,
//   location: eventData.location,
//   timeStamp: eventData.utc_timestamp_inms,
//   deviceId: eventData.environment_status__serial_number,
//   modelNumber: eventData.modelnumber,
// });

export const filterEventsData = (eventsData) =>
  eventsData.filter((e) => e[envEventDataKeys.MODEL_NUMBER] !== 'HC200');

// export const sortEventsData = (filteredEventsData) =>
//   filteredEventsData.sort((a, b) => {
//     const dateA = new Date(a.utc_timestamp_inms);
//     const dateB = new Date(b.utc_timestamp_inms);
//     return dateB - dateA;
//   });

export const sortEventsData = (eventsData, timestampKey) =>
  eventsData.toSorted((a, b) => {
    const dateA = DateTime.fromISO(a[timestampKey]);
    const dateB = DateTime.fromISO(b[timestampKey]);
    return dateB - dateA;
  });

// export const determineTempTrend = (sortedEventsData) => {
//   if (sortedEventsData.length > 1) {
//     const tempCurrent = sortedEventsData[0].environment_status__temperature;
//     const tempPrevious = sortedEventsData[1].environment_status__temperature;
//     return tempCurrent > tempPrevious ? 'up' : 'down';
//   }
//   return null;
// };

export const determineTempTrendFromValues = (observations) => {
  if (size(observations) <= 1) return null;
  const latestTemp = toNumber(last(observations));
  const previousTemp = toNumber(nth(observations, -2));
  return latestTemp > previousTemp ? 'up' : 'down';
};

export const determineTempTrend = (sortedEventsData) => {
  if (isEmpty(sortedEventsData)) return null;

  if (size(sortedEventsData) === 1) {
    const observations =
      sortedEventsData[0][`${envEventTypes.TEMPERATURE}__observations`].split(',');
    return determineTempTrendFromValues(observations);
  } else {
    const latestTemp = toNumber(
      last(sortedEventsData[0][`${envEventTypes.TEMPERATURE}__observations`].split(','))
    );
    const previousTemp = toNumber(
      last(sortedEventsData[1][`${envEventTypes.TEMPERATURE}__observations`].split(','))
    );
    return latestTemp > previousTemp ? 'up' : 'down';
  }
};

export const checkAllDevicesProcessed = (updatedData, userAccessoriesDeviceuuid) =>
  Object.keys(userAccessoriesDeviceuuid).every((deviceUuid) =>
    Object.keys(updatedData).includes(deviceUuid)
  );

export const reduceEventsData = (eventsData) => {
  const sortedEventsData = sortEventsData(eventsData, envEventDataKeys.TIMESTAMP);
  const latestEventData = sortedEventsData[0];

  const reducedEventsData = {
    // deviceId: eventData['deviceuuid'],
    modelNumber: latestEventData[envEventDataKeys.MODEL_NUMBER],
    location: latestEventData[envEventDataKeys.LOCATION],
    connectionStatus: latestEventData[envEventDataKeys.CONNECTED] ? 'CONNECTED' : 'DISCONNECTED',
    timeStamp: latestEventData[envEventDataKeys.TIMESTAMP],
  };

  forEach(envEventTypes, (userEventType) => {
    const filteredEventsData = eventsData
      .filter((e) => e[envEventDataKeys.MESSAGE_TYPE] === userEventType)
      .filter((e) => !isEmpty(e[`${userEventType}__observations`]));

    let sortedEventsData = [];
    let latestEventData = {};
    let latestObservation = null;

    if (!isEmpty(filteredEventsData)) {
      sortedEventsData = sortEventsData(filteredEventsData, `${userEventType}__timestamp`);
      latestEventData = sortedEventsData[0];
      latestObservation = toNumber(
        last(latestEventData[`${userEventType}__observations`].split(','))
      );
      reducedEventsData[`${userEventType}__timestamp`] =
        latestEventData[`${userEventType}__timestamp`];
    }

    switch (userEventType) {
      case envEventTypes.BATTERY:
        reducedEventsData['batteryLevel'] = latestObservation;
        break;
      case envEventTypes.HUMIDITY:
        reducedEventsData['humidity'] = latestObservation;
        break;
      case envEventTypes.LIGHT:
        reducedEventsData['ambientLight'] = latestObservation;
        break;
      case envEventTypes.TEMPERATURE:
        reducedEventsData['temperature'] = latestObservation;
        reducedEventsData['tempUnit'] = get(
          latestEventData,
          `${userEventType}__temperature_unit`,
          'F'
        );
        reducedEventsData['trend'] = determineTempTrend(sortedEventsData);
        break;
      default:
        break;
    }
  });

  return reducedEventsData;
};

export const isTimestampLatest = (timestamp, prevTimestamp, timezone) => {
  timestamp = timestamp.split('.')[0];
  prevTimestamp = prevTimestamp.split('.')[0];
  const latest = DateTime.fromISO(timestamp, { zone: timezone });
  const prev = DateTime.fromISO(prevTimestamp, { zone: timezone });
  return latest > prev;
};

export const processEventFromStream = (
  latestEvent,
  userEventsData,
  userAccessoriesDeviceuuid,
  timezone,
  setUserEventsData
) => {
  if (
    isEmpty(latestEvent) ||
    isEmpty(userEventsData) ||
    isEmpty(userAccessoriesDeviceuuid) ||
    isEmpty(timezone)
  ) {
    return;
  }

  const deviceuuid = get(latestEvent, envEventDataKeys.ENTITY_UUID, '');

  if (
    !Object.keys(userAccessoriesDeviceuuid).includes(deviceuuid) ||
    !Object.keys(userEventsData).includes(deviceuuid)
  ) {
    return;
  }

  setUserEventsData((prev) => {
    if (isEmpty(prev)) return prev;

    const userEventData = get(prev, `${deviceuuid}.data`);
    const eventType = latestEvent[envEventDataKeys.MESSAGE_TYPE];
    const utcTimestamp = latestEvent[envEventDataKeys.TIMESTAMP];
    const timestamp = latestEvent[`${eventType}__timestamp`];
    const prevTimestamp = userEventData[`${eventType}__timestamp`];
    let observations = latestEvent[`${eventType}__observations`];

    if (!isTimestampLatest(timestamp, prevTimestamp, timezone) || isEmpty(observations)) {
      return prev;
    }

    userEventData[`${eventType}__timestamp`] = timestamp;
    userEventData['timeStamp'] = utcTimestamp;
    if (isNumber(utcTimestamp)) {
      userEventData['timeStamp'] = DateTime.fromMillis(utcTimestamp).toISO({
        includeOffset: false,
      });
    }
    observations = isString(observations) ? observations.split(',') : observations;
    const latestObservation = toNumber(last(observations));

    switch (eventType) {
      case envEventTypes.BATTERY:
        userEventData['batteryLevel'] = latestObservation;
        break;
      case envEventTypes.HUMIDITY:
        userEventData['humidity'] = latestObservation;
        break;
      case envEventTypes.LIGHT:
        userEventData['ambientLight'] = latestObservation;
        break;
      case envEventTypes.TEMPERATURE:
        userEventData['temperature'] = latestObservation;
        userEventData['tempUnit'] = latestEvent[`${eventType}__temperature_unit`];
        userEventData['trend'] = determineTempTrendFromValues(observations);
        break;
      default:
        break;
    }

    return {
      ...prev,
      [deviceuuid]: {
        ...prev[deviceuuid],
        data: userEventData,
      },
    };
  });
};

export const processWeatherData = (data, tempUnit, setOutdoorWeather) => {
  if (!data.length) return;
  const weatherData = data[0];

  const isFahrenheit = tempUnit === 'F';
  const temperature = isFahrenheit
    ? Math.round(weatherData.temperature.imperial.value)
    : Math.round(weatherData.temperature.metric.value);
  const unit = isFahrenheit ? 'F' : 'C';

  const finalData = {
    temp: temperature,
    unit: unit,
    prec: weatherData.hasprecipitation,
    icon: weatherData.weathericon,
    humidity: weatherData.relativehumidity,
    time: weatherData.localobservationdatetime,
  };

  setOutdoorWeather(finalData);
  return finalData;
};

export const fetchPastTrendData = (
  useruuid,
  deviceUuid,
  userEventsApi,
  trans,
  setPastDataProcessed,
  timezoneOffset,
  setDeviceUuidToShowTrend,
  selectedRange,
  convertTemperature,
  userTempUnit,
  view,
  setIndoorTrendDataLoader = {},
  setEnvHistoricalData,
  propUserEvents,
  sensorDetails
) => {
  const successCallback = (data) => {
    setEnvHistoricalData(data);
    if (sensorDetails.devicetype === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR) {
      processPastTrendData(
        data['user_events'],
        setPastDataProcessed,
        setDeviceUuidToShowTrend,
        deviceUuid,
        timezoneOffset,
        convertTemperature,
        userTempUnit,
        setIndoorTrendDataLoader
      );
    } else if (sensorDetails.devicetype === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR) {
      processTempHumiData({
        data: data['user_events'],
        setPastDataProcessed,
        setDeviceUuidToShowTrend,
        deviceUuid,
        timezoneOffset,
        convertTemperature,
        userTempUnit,
        setIndoorTrendDataLoader,
      });
    }
  };
  const errorCallback = (error) => {
    setIndoorTrendDataLoader(false);
    console.error('Error while getting past data: ', error);
  };

  const today = new Date();
  // const rangeDays =
  // selectedRange === 'today'
  //   ? 1
  //   : selectedRange === 'weekly'
  //   ? 7
  //   : selectedRange === 'monthly'
  //   ? 30
  //   : 30;
  const rangeDays = 30;
  const endDate = today.toISOString();
  const startDate = new Date(today.setDate(today.getDate() - rangeDays)).toISOString();

  const eventTypes = [envEventTypes.TEMPERATURE, envEventTypes.HUMIDITY, envEventTypes.LIGHT]; //view === 'temperature' ? [envEventTypes.TEMPERATURE] : [envEventTypes.HUMIDITY];

  if (!propUserEvents) {
    userDeviceEventsByTypesAndTimeRange(
      userEventsApi,
      {
        useruuid,
        startdatetime: startDate,
        enddatetime: endDate,
        deviceuuid: [deviceUuid],
        eventtypes: eventTypes,
      },
      apiCallback({
        translator: trans,
        failCallback: errorCallback,
        errorCallback,
        successCallback,
      })
    );
  } else {
    successCallback({
      user_events: propUserEvents,
    });
  }
};

export const fetchPastOutdoorData = (
  weatherApi,
  trans,
  setPastDataProcessed,
  timezoneOffset,
  useruuid,
  selectedRange,
  convertTemperature,
  userTempUnit,
  setOutdoorTrendDataLoader = {}
) => {
  const successCallback = (data) => {
    processPastOutdoorData(
      data['historical'],
      setPastDataProcessed,
      timezoneOffset,
      convertTemperature,
      userTempUnit,
      setOutdoorTrendDataLoader
    );
  };
  const errorCallback = (error) => {
    setOutdoorTrendDataLoader(false);
    console.error('Error while getting past outdoor data: ', error);
  };

  const today = new Date();
  // const rangeDays =
  //   selectedRange === 'today'
  //     ? 1
  //     : selectedRange === 'weekly'
  //     ? 7
  //     : selectedRange === 'monthly'
  //     ? 30
  //     : 30;
  const rangeDays = 30;
  const endDate = today.toISOString();
  const startDate = new Date(today.setDate(today.getDate() - rangeDays)).toISOString();

  weatherApi.getHistoricalByTimeRange(
    startDate,
    endDate,
    { useruuid: useruuid },
    apiCallback({
      translator: trans,
      failCallback: errorCallback,
      errorCallback,
      successCallback,
    })
  );
};

const processPastOutdoorData = (
  data,
  setPastDataProcessed,
  timezoneOffset,
  convertTemperature,
  userTempUnit,
  setOutdoorTrendDataLoader
) => {
  const extractDate = (datetime) => datetime.split('T')[0];

  const dailyData = {};

  if (!data || !Array.isArray(data) || data.length === 0) {
    console.warn('Invalid or empty data provided to processPastOutdoorData:', data);
    setOutdoorTrendDataLoader(false);
    return {};
  }

  data.forEach((entry) => {
    // Bucket by the fixed epoch timestamp, not localobservationdatetime which can drift a few minutes before the hour boundary
    const localMoment = moment(entry.utc_timestamp_inms).utcOffset(timezoneOffset);

    const date = localMoment.format('YYYY-MM-DD');
    const hour = localMoment.format('HH');
    const deviceId = 'outdoor';

    if (!dailyData[date]) {
      dailyData[date] = {};
    }

    if (!dailyData[date][deviceId]) {
      dailyData[date][deviceId] = {
        environData: {
          hourlyTemp: {},
          hourlyHumidity: {},
          hourlyLight: {},
        },
        serialNumber: 'outdoor',
      };
    }

    if (!dailyData[date][deviceId].environData.hourlyTemp[hour]) {
      dailyData[date][deviceId].environData.hourlyTemp[hour] = 0;
      dailyData[date][deviceId].environData.hourlyHumidity[hour] = 0;
      dailyData[date][deviceId].environData.hourlyLight[hour] = 0;
      dailyData[date][deviceId].environData.hourlyTemp[hour + '_count'] = 0;
      dailyData[date][deviceId].environData.hourlyHumidity[hour + '_count'] = 0;
      dailyData[date][deviceId].environData.hourlyLight[hour + '_count'] = 0;
    }

    dailyData[date][deviceId].environData.hourlyTemp[hour] +=
      userTempUnit === 'F' ? entry.temperature.imperial.value : entry.temperature.metric.value;
    dailyData[date][deviceId].environData.hourlyHumidity[hour] += entry.relativehumidity;
    dailyData[date][deviceId].environData.hourlyLight[hour] += 0;
    dailyData[date][deviceId].environData.hourlyTemp[hour + '_count'] += 1;
    dailyData[date][deviceId].environData.hourlyHumidity[hour + '_count'] += 1;
    dailyData[date][deviceId].environData.hourlyLight[hour + '_count'] += 1;
  });

  Object.keys(dailyData).forEach((date) => {
    Object.keys(dailyData[date]).forEach((deviceId) => {
      const deviceData = dailyData[date][deviceId];
      Object.keys(deviceData.environData.hourlyTemp).forEach((hour) => {
        if (!hour.includes('_count')) {
          const count = deviceData.environData.hourlyTemp[hour + '_count'];
          deviceData.environData.hourlyTemp[hour] = parseFloat(
            (deviceData.environData.hourlyTemp[hour] / count).toFixed(1)
          );
          deviceData.environData.hourlyHumidity[hour] = parseFloat(
            (deviceData.environData.hourlyHumidity[hour] / count).toFixed(1)
          );
          deviceData.environData.hourlyLight[hour] = parseFloat(
            (deviceData.environData.hourlyLight[hour] / count).toFixed(1)
          );
          delete deviceData.environData.hourlyTemp[hour + '_count'];
          delete deviceData.environData.hourlyHumidity[hour + '_count'];
          delete deviceData.environData.hourlyLight[hour + '_count'];
        }
      });
    });
  });
  setPastDataProcessed((prevState) => ({
    ...prevState,
    outdoorDataFetched: dailyData,
  }));
  setOutdoorTrendDataLoader(false);
  return dailyData;
};

const processTempHumiData = ({
  data,
  setPastDataProcessed,
  setDeviceUuidToShowTrend,
  deviceUuid,
  timezoneOffset,
  convertTemperature,
  userTempUnit,
  setIndoorTrendDataLoader,
}) => {
  const dailyData = {};

  if (!Array.isArray(data) || data.length === 0) {
    console.warn('Invalid or empty data provided to processTempHumiData:', data);
    setIndoorTrendDataLoader(false);
    return {};
  }

  data.forEach((entry) => {
    // Localize timestamp, then group by YYYY-MM-DD and HH
    const localMoment = moment(entry.utc_timestamp_inms).utcOffset(timezoneOffset);
    const date = localMoment.format('YYYY-MM-DD');
    const hour = localMoment.format('HH');
    const devId = entry.deviceuuid;

    if (!dailyData[date]) dailyData[date] = {};
    if (!dailyData[date][devId]) {
      dailyData[date][devId] = {
        environData: {
          hourlyTemp: {},
          hourlyHumidity: {},
        },
        // Use BLE identifiers if available
        serialNumber: entry.deviceid || entry.devicename || null,
      };
    }

    // Initialize hour buckets and counters
    const ensureHour = () => {
      if (!dailyData[date][devId].environData.hourlyTemp[hour]) {
        dailyData[date][devId].environData.hourlyTemp[hour] = 0;
        dailyData[date][devId].environData.hourlyHumidity[hour] = 0;
        dailyData[date][devId].environData.hourlyTemp[`${hour}_count`] = 0;
        dailyData[date][devId].environData.hourlyHumidity[`${hour}_count`] = 0;
      }
    };

    // Temperature
    if (Number.isFinite(entry.temperature)) {
      ensureHour();
      const apiUnit = (entry.temperature_unit || 'F').toUpperCase();
      const val = convertTemperature(entry.temperature, apiUnit, userTempUnit);
      if (Number.isFinite(val)) {
        dailyData[date][devId].environData.hourlyTemp[hour] += val;
        dailyData[date][devId].environData.hourlyTemp[`${hour}_count`] += 1;
      }
    }

    // Humidity
    if (Number.isFinite(entry.humidity)) {
      ensureHour();
      dailyData[date][devId].environData.hourlyHumidity[hour] += entry.humidity;
      dailyData[date][devId].environData.hourlyHumidity[`${hour}_count`] += 1;
    }

    // Prefer latest non-null serial number
    if (entry.deviceid || entry.devicename) {
      dailyData[date][devId].serialNumber = entry.deviceid || entry.devicename;
    }
  });

  // Average each hour bucket and remove counters
  Object.keys(dailyData).forEach((date) => {
    Object.keys(dailyData[date]).forEach((devId) => {
      const buckets = dailyData[date][devId].environData;
      Object.keys(buckets.hourlyTemp).forEach((hour) => {
        if (hour.includes('_count')) return;
        const tCount = buckets.hourlyTemp[`${hour}_count`] || 0;
        const hCount = buckets.hourlyHumidity[`${hour}_count`] || 0;

        buckets.hourlyTemp[hour] =
          tCount > 0 ? parseFloat((buckets.hourlyTemp[hour] / tCount).toFixed(1)) : null;
        buckets.hourlyHumidity[hour] =
          hCount > 0 ? parseFloat((buckets.hourlyHumidity[hour] / hCount).toFixed(1)) : null;

        delete buckets.hourlyTemp[`${hour}_count`];
        delete buckets.hourlyHumidity[`${hour}_count`];
      });
    });
  });

  // Match processPastTrendData: set trendDataFetched and helpers
  setPastDataProcessed((prev) => ({
    ...prev,
    trendDataFetched: dailyData,
  }));
  setDeviceUuidToShowTrend(deviceUuid);
  setIndoorTrendDataLoader(false);

  return dailyData;
};

const processPastTrendData = (
  data,
  setPastDataProcessed,
  setDeviceUuidToShowTrend,
  deviceUuid,
  timezoneOffset,
  convertTemperature,
  userTempUnit,
  setIndoorTrendDataLoader
) => {
  const dailyData = {};

  if (!data || !Array.isArray(data) || data.length === 0) {
    console.warn('Invalid or empty data provided to processPastTrendData:', data);
    setIndoorTrendDataLoader(false);
    return {};
  }

  data.forEach((event) => {
    const localMoment = moment(event.utc_timestamp_inms).utcOffset(timezoneOffset);
    const date = localMoment.format('YYYY-MM-DD');
    const hour = localMoment.format('HH');
    const deviceId = event.deviceuuid;

    const getLastObservation = (obsStr) => {
      if (!obsStr) return null;
      const arr = obsStr.split(',');
      return arr.length ? parseFloat(arr[arr.length - 1]) : null;
    };

    let temp = null,
      humidity = null,
      light = null,
      tempUnit = event.temperature_unit || null,
      serialNumber = event.serial_number || null;

    if (event.message_type === envEventTypes.TEMPERATURE) {
      temp = getLastObservation(event.observations);
      tempUnit = event.temperature_unit || 'F';
    } else if (event.message_type === envEventTypes.HUMIDITY) {
      humidity = getLastObservation(event.observations);
    }

    if (!dailyData[date]) {
      dailyData[date] = {};
    }

    if (!dailyData[date][deviceId]) {
      dailyData[date][deviceId] = {
        environData: {
          hourlyTemp: {},
          hourlyHumidity: {},
        },
        serialNumber: serialNumber,
      };
    }

    if (serialNumber) {
      dailyData[date][deviceId].serialNumber = serialNumber;
    }

    if (temp !== null || humidity !== null || light !== null) {
      if (!dailyData[date][deviceId].environData.hourlyTemp[hour]) {
        dailyData[date][deviceId].environData.hourlyTemp[hour] = 0;
        dailyData[date][deviceId].environData.hourlyHumidity[hour] = 0;
        dailyData[date][deviceId].environData.hourlyTemp[hour + '_count'] = 0;
        dailyData[date][deviceId].environData.hourlyHumidity[hour + '_count'] = 0;
      }
    }

    if (temp !== null && !isNaN(temp)) {
      dailyData[date][deviceId].environData.hourlyTemp[hour] += convertTemperature(
        temp,
        tempUnit,
        userTempUnit
      );
      dailyData[date][deviceId].environData.hourlyTemp[hour + '_count'] += 1;
    }
    if (humidity !== null && !isNaN(humidity)) {
      dailyData[date][deviceId].environData.hourlyHumidity[hour] += humidity;
      dailyData[date][deviceId].environData.hourlyHumidity[hour + '_count'] += 1;
    }
  });

  Object.keys(dailyData).forEach((date) => {
    Object.keys(dailyData[date]).forEach((deviceId) => {
      const deviceData = dailyData[date][deviceId];
      Object.keys(deviceData.environData.hourlyTemp).forEach((hour) => {
        if (!hour.includes('_count')) {
          const count = deviceData.environData.hourlyTemp[hour + '_count'];
          const humidityCount = deviceData.environData.hourlyHumidity[hour + '_count'];
          deviceData.environData.hourlyTemp[hour] =
            count > 0
              ? parseFloat((deviceData.environData.hourlyTemp[hour] / count).toFixed(1))
              : null;
          deviceData.environData.hourlyHumidity[hour] =
            humidityCount > 0
              ? parseFloat((deviceData.environData.hourlyHumidity[hour] / humidityCount).toFixed(1))
              : null;
          delete deviceData.environData.hourlyTemp[hour + '_count'];
          delete deviceData.environData.hourlyHumidity[hour + '_count'];
        }
      });
    });
  });

  setPastDataProcessed((prevState) => ({
    ...prevState,
    trendDataFetched: dailyData,
  }));
  setDeviceUuidToShowTrend(deviceUuid);
  setIndoorTrendDataLoader(false);
};

export const onTrendIconClick = (
  useruuid,
  deviceUuid,
  userEventsApi,
  weatherApi,
  trans,
  setPastDataProcessed,
  timezoneOffset,
  setDeviceUuidToShowTrend,
  selectedRange,
  convertTemperature,
  userTempUnit,
  view,
  setOutdoorTrendDataLoader,
  setIndoorTrendDataLoader,
  location = null,
  setGraphModalTitle,
  deviceId = null,
  setModalTitleDeviceLocationObj,
  batteryStatus,
  connectionStatus,
  grey,
  setEnvHistoricalData,
  setShowPastTrend,
  propUserEvents,
  sensorDetails
) => {
  setModalTitleDeviceLocationObj({
    location: location,
    deviceId: deviceId,
    batteryStatus: batteryStatus,
    connectionStatus: connectionStatus,
    grey: grey,
  });
  setShowPastTrend(true);
  setOutdoorTrendDataLoader(true);
  setIndoorTrendDataLoader(true);
  setGraphModalTitle(location || 'Trend Data');
  fetchPastTrendData(
    useruuid,
    deviceUuid,
    userEventsApi,
    trans,
    // setPastDataProcessed,
    (data) => {
      setPastDataProcessed(data);
      setDeviceUuidToShowTrend(deviceUuid);
    },
    timezoneOffset,
    setDeviceUuidToShowTrend,
    selectedRange,
    convertTemperature,
    userTempUnit,
    view,
    setIndoorTrendDataLoader,
    setEnvHistoricalData,
    propUserEvents,
    sensorDetails
  );
  fetchPastOutdoorData(
    weatherApi,
    trans,
    (data) => {
      setPastDataProcessed(data);
    },
    timezoneOffset,
    useruuid,
    selectedRange,
    convertTemperature,
    userTempUnit,
    setOutdoorTrendDataLoader
  );
};

export const getOutdoorSensorFormattedData = (outdoorData, tempUnit) => {
  const data = processWeatherData(outdoorData, tempUnit, () => {});
  return {
    utc_timestamp_inms: data.time,
    accessory_state_ble_temperature_sensor__temperature: data.temp,
    accessory_state_ble_temperature_sensor__humidity: data.humidity,
    accessory_state_ble_temperature_sensor__temperature_unit: data.unit,
    weatherId: data.icon,
    ...data,
  };
};

function formatTimestamp(timestamp, timezone, loginUserTimeformat, trans) {
  if (timestamp == null || timestamp == undefined) {
    return { day: '-', time: '-', difference: '-' };
  }
  timezone = timezone.split(' ')[0];
  const now = moment().tz(timezone);
  const inputTime = moment.tz(timestamp, timezone);

  let time = '';
  if (loginUserTimeformat?.is24hoursformat)
    time = inputTime.format('HH:mm'); // Format time as '09:40'
  else time = inputTime.format('h:mm A'); // Format time as '9:40 AM'

  const difference = moment.duration(inputTime.diff(now));
  let differenceText;

  if (difference.asMilliseconds() < 0) {
    // Past data
    const pastDifference = moment.duration(now.diff(inputTime));
    differenceText =
      pastDifference.asMinutes() < 1
        ? trans('JUST_NOW_L')
        : pastDifference.asMinutes() < 60
          ? `${Math.round(pastDifference.asMinutes())} ${trans('MINAGO_L')} `
          : pastDifference.asHours() < 24
            ? `${Math.round(pastDifference.asHours())} ${trans('HOURSAGO_L')} `
            : `${Math.round(pastDifference.asDays())} ${trans('DAYSAGO_L')} `;
  } else {
    // Future data
    differenceText =
      difference.asMinutes() < 1
        ? trans('LATER')
        : difference.asMinutes() < 60
          ? `${trans('IN')} ${Math.round(difference.asMinutes())} ${trans('MIN_L')} `
          : difference.asHours() < 24
            ? `${trans('IN')} ${Math.round(difference.asHours())} ${trans('HOURS_L')} `
            : `${trans('IN')} ${Math.round(difference.asDays())} ${trans('DAYS_L')} `;
  }

  let day;
  let date = inputTime.format('D/MM/YY'); // Format as 'D/MM/YY' like '3/31/25'
  let weekDay = inputTime.format('ddd'); // Format as 'Tue', 'Wed', etc.

  if (now.isSame(inputTime, 'day')) {
    day = 'TODAY';
  } else if (now.subtract(1, 'day').isSame(inputTime, 'day')) {
    day = 'YESTERDAY';
  } else {
    day = inputTime.format('ddd M/D'); // Format as 'Sun 12/1'
  }

  return {
    day,
    weekDay,
    time,
    date,
    difference: differenceText,
  };
}

export const getTempHumiSummaryData = ({
  userEvents,
  deviceUuid,
  selectedRange,
  timezone,
  displayTempUnit,
}) => {
  const summary = {
    temp: { high: null, low: null },
    humidity: { high: null, low: null },
    light: { high: null, low: null }, // BLE payload has no light; keep keys consistent
  };

  if (!Array.isArray(userEvents) || !deviceUuid) return summary;

  const tz = (timezone || 'UTC').split(' ')[0];
  const now = moment().tz(tz);
  const today = now.clone().startOf('day');

  const inSelectedRange = (iso) => {
    const m = moment.tz(iso, tz);
    if (selectedRange === 'today') {
      const cutoff = now.clone().subtract(24, 'hours');
      return m.isAfter(cutoff) && m.isBefore(now);
    } else if (selectedRange === 'weekly') {
      const cutoff = today.clone().subtract(7, 'days');
      return m.isAfter(cutoff);
    } else if (selectedRange === 'monthly') {
      const cutoff = today.clone().subtract(30, 'days');
      return m.isAfter(cutoff);
    }
    return true;
  };

  // Filter by device and time range
  const rows = userEvents
    .filter(
      (e) =>
        e?.deviceuuid === deviceUuid &&
        e?.utc_timestamp_inms &&
        inSelectedRange(e.utc_timestamp_inms)
    )
    .sort((a, b) => new Date(b.utc_timestamp_inms) - new Date(a.utc_timestamp_inms));

  if (!rows.length) return summary;

  const temps = [];
  const hums = [];

  rows.forEach((e) => {
    // Temperature
    const t = Number(e?.temperature);
    if (Number.isFinite(t)) {
      const apiUnit = String(e?.temperature_unit || displayTempUnit || 'F').toUpperCase();
      const targetUnit = String(displayTempUnit || apiUnit).toUpperCase();
      const val = apiUnit === targetUnit ? t : convertTemperature(t, apiUnit, targetUnit);
      if (Number.isFinite(val)) temps.push(val);
    }

    // Humidity
    const h = Number(e?.humidity);
    if (Number.isFinite(h)) hums.push(h);
  });

  // Compute highs/lows
  if (temps.length) {
    let maxT = temps[0];
    let minT = temps[0];
    for (let i = 1; i < temps.length; i++) {
      const v = temps[i];
      if (v > maxT) maxT = v;
      if (v < minT) minT = v;
    }
    summary.temp.high = maxT;
    summary.temp.low = minT;
  }

  if (hums.length) {
    let maxH = hums[0];
    let minH = hums[0];
    for (let i = 1; i < hums.length; i++) {
      const v = hums[i];
      if (v > maxH) maxH = v;
      if (v < minH) minH = v;
    }
    summary.humidity.high = maxH;
    summary.humidity.low = minH;
  }

  return summary;
};

export const getSummaryData = ({
  userEvents,
  deviceUuid,
  selectedRange,
  timezone,
  loginUserTimeformat,
  trans,
  displayTempUnit,
}) => {
  const groupedData = userEvents
    .filter((item) => item.deviceuuid === deviceUuid)
    .filter((item) => {
      // Filter based on selectedRange
      const itemMoment = moment.tz(item.utc_timestamp_inms, timezone.split(' ')[0]);
      const now = moment().tz(timezone.split(' ')[0]);
      const today = now.clone().startOf('day');

      if (selectedRange === 'today') {
        const cutoff = now.clone().subtract(24, 'hours');
        return itemMoment.isAfter(cutoff) && itemMoment.isBefore(now);
      } else if (selectedRange === 'weekly') {
        const cutoff = today.clone().subtract(7, 'days');
        return itemMoment.isAfter(cutoff);
      } else if (selectedRange === 'monthly') {
        const cutoff = today.clone().subtract(30, 'days');
        return itemMoment.isAfter(cutoff);
      }
      return true;
    })
    .sort((a, b) => new Date(b.utc_timestamp_inms) - new Date(a.utc_timestamp_inms))
    .reduce((acc, item) => {
      const timestampToMinute = item.utc_timestamp_inms.substring(0, 16);

      if (!acc[timestampToMinute]) {
        let dateTimeObj = formatTimestamp(
          item.utc_timestamp_inms,
          timezone,
          loginUserTimeformat,
          trans
        );
        let day = dateTimeObj['day'];
        let time = dateTimeObj['time'];
        let date = dateTimeObj['date'];
        let weekDay = dateTimeObj['weekDay'];

        acc[timestampToMinute] = {
          utc_timestamp: item.utc_timestamp_inms,
          day: day,
          time: time,
          date: date,
          weekDay: weekDay,
          deviceuuid: item.deviceuuid,
          temp_observations: '',
          humidity_observations: '',
          light_observations: '',
          temperature_unit: '',
        };
      }

      // Filter observations based on message_type
      if (item.message_type === 'sensor_temperature_report') {
        acc[timestampToMinute].temp_observations = item.observations || '';
        acc[timestampToMinute].temperature_unit = item.temperature_unit || '';
      } else if (item.message_type === 'sensor_humidity_report') {
        acc[timestampToMinute].humidity_observations = item.observations || '';
      } else if (item.message_type === 'sensor_light_report') {
        acc[timestampToMinute].light_observations = item.observations || '';
      }

      return acc;
    }, {});

  const summary = {
    temp: { high: null, low: null },
    humidity: { high: null, low: null },
    light: { high: null, low: null },
  };

  const allValues = {
    temp: [],
    humidity: [],
    light: [],
  };

  Object.values(groupedData).forEach((data) => {
    // Parse TEMPERATURE observations
    if (data.temp_observations) {
      try {
        const tempValues = data.temp_observations.split(',');
        const apiUnit = String(data.temperature_unit || displayTempUnit).toUpperCase();
        const needsConversion = apiUnit !== displayTempUnit;

        tempValues.forEach((val) => {
          const numVal = parseFloat(val);
          if (!isNaN(numVal)) {
            allValues.temp.push(
              needsConversion ? convertTemperature(numVal, apiUnit, displayTempUnit) : numVal
            );
          }
        });
      } catch (e) {
        console.error('Error parsing temperature for summary:', e);
      }
    }

    // Parse humidity observations
    if (data.humidity_observations) {
      try {
        const humidityValues = data.humidity_observations.split(',');
        humidityValues.forEach((val) => {
          const numVal = parseFloat(val);
          if (!isNaN(numVal)) {
            allValues.humidity.push(numVal);
          }
        });
      } catch (e) {
        console.error('Error parsing humidity for summary:', e);
      }
    }

    // Parse light observations
    if (data.light_observations) {
      try {
        const lightValues = data.light_observations.split(',');
        lightValues.forEach((val) => {
          const numVal = parseFloat(val);
          if (!isNaN(numVal)) {
            allValues.light.push(numVal);
          }
        });
      } catch (e) {
        console.error('Error parsing light for summary:', e);
      }
    }
  });
  if (allValues.temp.length > 0) {
    let maxT = allValues.temp[0];
    let minT = allValues.temp[0];
    for (let i = 1; i < allValues.temp.length; i++) {
      const v = allValues.temp[i];
      if (v > maxT) maxT = v;
      if (v < minT) minT = v;
    }
    summary.temp.high = maxT;
    summary.temp.low = minT;
  }

  if (allValues.humidity.length > 0) {
    let maxH = allValues.humidity[0];
    let minH = allValues.humidity[0];
    for (let i = 1; i < allValues.humidity.length; i++) {
      const v = allValues.humidity[i];
      if (v > maxH) maxH = v;
      if (v < minH) minH = v;
    }
    summary.humidity.high = maxH;
    summary.humidity.low = minH;
  }

  if (allValues.light.length > 0) {
    let maxL = allValues.light[0];
    let minL = allValues.light[0];
    for (let i = 1; i < allValues.light.length; i++) {
      const v = allValues.light[i];
      if (v > maxL) maxL = v;
      if (v < minL) minL = v;
    }
    summary.light.high = maxL;
    summary.light.low = minL;
  }

  return summary;
};
